import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import PhoneFrame from "@/components/PhoneFrame";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import GoogleButton from "@/components/GoogleButton";
import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import { api } from "@/lib/api";
import { ArrowLeft, Mail } from "lucide-react";

/**
 * Passwordless email-OTP auth.
 *
 * Flow:
 *   1. User enters email → POST /auth/email/request-otp → toast + resend cooldown
 *   2. User enters the 6-digit code from their inbox → POST /auth/email/verify-otp
 *      → server returns { access_token, user } → we set the auth context and
 *      route into /app/home.
 *
 * There is no password field — Impo is passwordless. Google OAuth is offered
 * as a one-tap alternative alongside the OTP flow.
 */

const RESEND_COOLDOWN_DEFAULT = 60;

export default function Auth() {
  const [sp] = useSearchParams();
  // Mode is purely for the copy at the top ("Welcome back" vs "Create your vault").
  // The actual auth flow is identical for both — server creates the user on first
  // successful OTP verification if they don't exist yet.
  const [mode, setMode] = useState(sp.get("mode") === "login" ? "login" : "register");

  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [stage, setStage] = useState("enter-email"); // "enter-email" | "enter-otp"
  const [loading, setLoading] = useState(false);
  const [resendIn, setResendIn] = useState(0); // seconds until user can resend

  const otpInputRef = useRef(null);
  const nav = useNavigate();
  const { setAuthFromToken } = useAuth();

  useEffect(() => {
    setMode(sp.get("mode") === "login" ? "login" : sp.get("mode") === "register" ? "register" : mode);
    // eslint-disable-next-line
  }, [sp]);

  // Auto-focus the OTP field when entering the code-entry stage.
  useEffect(() => {
    if (stage === "enter-otp") {
      // Small delay so the input is in the DOM after the state flip.
      const t = setTimeout(() => otpInputRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
  }, [stage]);

  // Countdown ticker for the resend cooldown.
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setInterval(() => setResendIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  const requestOtp = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const trimmed = (email || "").trim().toLowerCase();
    if (!trimmed) { toast.error("Please enter your email"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/email/request-otp", { email: trimmed });
      setEmail(trimmed);
      setStage("enter-otp");
      setOtp("");
      setResendIn(data?.resend_after || RESEND_COOLDOWN_DEFAULT);
      toast.success(data?.message || "We've sent a 6-digit code to your inbox.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't send the code. Please try again.");
    } finally { setLoading(false); }
  };

  const verifyOtp = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (otp.length !== 6) { toast.error("Enter the 6-digit code from your email"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/email/verify-otp", { email, otp });
      setAuthFromToken(data.access_token, data.user);
      toast.success(data.created_new_user ? "Welcome to Impo!" : "Welcome back!");
      nav("/app/home", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Invalid code. Please try again.");
    } finally { setLoading(false); }
  };

  const changeEmail = () => {
    setStage("enter-email");
    setOtp("");
    setResendIn(0);
  };

  return (
    <PhoneFrame>
      <div className="flex flex-col h-full min-h-[100dvh]">
        <div className="px-6 pt-14 pb-6">
          <div className="flex items-center justify-between">
            <Logo size={26} />
            <ThemeToggle size="sm" />
          </div>
          <h1 className="font-display font-semibold text-2xl mt-6">
            {mode === "login" ? "Welcome back" : "Create your vault"}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {mode === "login"
              ? "Sign in with your email — we'll send you a one-time code."
              : "One private home for what matters. Just your email, no password."}
          </p>
        </div>

        <div className="flex-1 px-6 space-y-4">
          <GoogleButton next="/app/home" />

          {/* Divider */}
          <div className="flex items-center gap-3 text-[11px] uppercase tracking-widest text-muted-foreground">
            <div className="h-px flex-1 bg-border" />
            <span>or with email</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          {stage === "enter-email" ? (
            <form onSubmit={requestOtp} className="space-y-4" data-testid="auth-email-stage">
              <div>
                <Label className="text-xs" htmlFor="email">Email address</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoFocus
                  data-testid="auth-email-input"
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  We'll send a 6-digit code to your inbox. It's valid for 5 minutes.
                </p>
              </div>
              <Button
                type="submit"
                disabled={loading || !email.trim()}
                className="w-full h-12 text-base"
                data-testid="auth-send-otp-button"
              >
                {loading ? "Sending…" : "Send code"}
              </Button>
            </form>
          ) : (
            <form onSubmit={verifyOtp} className="space-y-4" data-testid="auth-otp-stage">
              <button
                type="button"
                onClick={changeEmail}
                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                data-testid="auth-otp-back"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> Change email
              </button>

              <div className="rounded-xl border border-border bg-card p-3 flex items-start gap-3">
                <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary grid place-items-center shrink-0">
                  <Mail className="h-4 w-4" />
                </div>
                <div className="text-xs leading-relaxed">
                  <div className="font-medium text-foreground">Check your inbox</div>
                  <div className="text-muted-foreground">
                    We sent a 6-digit code to <span className="font-mono text-foreground">{email}</span>. Codes may take up to a minute; check spam if you don't see it.
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-xs" htmlFor="otp">Enter the 6-digit code</Label>
                <Input
                  id="otp"
                  ref={otpInputRef}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="123456"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  required
                  data-testid="auth-otp-input"
                  className="tracking-[0.4em] font-mono text-xl text-center"
                />
              </div>

              <Button
                type="submit"
                disabled={loading || otp.length !== 6}
                className="w-full h-12 text-base"
                data-testid="auth-verify-otp-button"
              >
                {loading ? "Verifying…" : "Verify & continue"}
              </Button>

              <button
                type="button"
                onClick={requestOtp}
                disabled={loading || resendIn > 0}
                className="w-full text-xs text-muted-foreground py-1 disabled:opacity-50"
                data-testid="auth-resend-otp"
              >
                {resendIn > 0
                  ? `Resend code in ${resendIn}s`
                  : "Didn't get it? Resend code"}
              </button>
            </form>
          )}
        </div>

        <div className="p-6 text-center space-y-2">
          <button
            onClick={() => setMode(mode === "login" ? "register" : "login")}
            className="text-sm text-muted-foreground hover:text-foreground"
            data-testid="auth-toggle-mode"
          >
            {mode === "login"
              ? "New to Impo? Create your vault"
              : "Already have an account? Sign in"}
          </button>
          <div className="text-[11px] text-muted-foreground">
            By continuing, you agree to our{" "}
            <Link to="/terms" className="underline hover:text-foreground" data-testid="auth-terms-link">Terms</Link>
            {" "}and{" "}
            <Link to="/privacy" className="underline hover:text-foreground" data-testid="auth-privacy-link">Privacy Policy</Link>.
          </div>
        </div>
      </div>
    </PhoneFrame>
  );
}
